import { Box, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import { homePastEventImages } from '../../data/homeContent'
import { pastEventsCurve } from '../../assets/index'
import {
  HOME_MOBILE_MQ,
  homeMobilePaddingX,
  homeSectionPy,
  homeSectionTitleSx,
} from './homeMobileLayout'
import { SLIDER_MARQUEE_DURATION } from '../../theme/slider'

const SLIDE_WIDTH = 380
const SLIDE_HEIGHT = 540
const SLIDE_GAP = 30
const PAST_EVENTS_CLIP_ID = 'past-events-gallery-curve-clip'

const marquee = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`

function FigmaCurve({ position }) {
  const isTop = position === 'top'
  return (
    <Box
      component="img"
      src={pastEventsCurve}
      alt=""
      aria-hidden
      sx={{
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        width: { xs: 'calc(100% + 80px)', lg: 2000 },
        height: { xs: 100, sm: 140, md: 170, lg: 200 },
        pointerEvents: 'none',
        zIndex: 1,
        display: 'block',
        maxWidth: 'none',
        ...(isTop ? { top: 0 } : { bottom: 0 }),
        '@media (min-width: 426px)': { display: 'block' },
        [HOME_MOBILE_MQ]: { display: 'none' },
      }}
    />
  )
}

function GallerySlide({ src, alt }) {
  return (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: { xs: 280, sm: 320, md: 340, lg: SLIDE_WIDTH },
        height: { xs: 400, sm: 450, md: 480, lg: SLIDE_HEIGHT },
        borderRadius: '20px',
        overflow: 'hidden',
        bgcolor: '#fffefa',
        boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.12)',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          borderRadius: '20px',
        }}
      />
    </Box>
  )
}

function MobileGridImage({ src, alt }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: 143,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: '#fffefa',
        boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.12)',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  )
}

export default function PastEventsGallery() {
  const loopImages = [...homePastEventImages, ...homePastEventImages]
  const mobileImages = homePastEventImages.slice(0, 6)

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        pt: homeSectionPy,
        pb: homeSectionPy,
        overflow: 'hidden',
        boxSizing: 'border-box',
        bgcolor: '#fff',
        [HOME_MOBILE_MQ]: { bgcolor: '#fffefa' },
      }}
    >
      <FigmaCurve position="top" />
      <FigmaCurve position="bottom" />

      <Box
        component="svg"
        aria-hidden
        sx={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          display: { xs: 'none', sm: 'block' },
          [HOME_MOBILE_MQ]: { display: 'none' },
        }}
      >
        <defs>
          <clipPath id={PAST_EVENTS_CLIP_ID} clipPathUnits="objectBoundingBox">
            <path d="M 0,0.07 Q 0.5,0.22 1,0.07 L 1,0.93 Q 0.5,0.78 0,0.93 Z" />
          </clipPath>
        </defs>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          maxWidth: 1920,
          mx: 'auto',
          px: homeMobilePaddingX,
        }}
      >
        <Typography
          component="h2"
          sx={{
            ...homeSectionTitleSx,
            textAlign: { xs: 'left', lg: 'center' },
            mb: { xs: '32px', sm: 5, lg: '76px' },
            [HOME_MOBILE_MQ]: { fontSize: 28, mb: '32px', textAlign: 'left' },
          }}
        >
          Past Events Glimpse
        </Typography>

        <Box
          sx={{
            display: 'none',
            [HOME_MOBILE_MQ]: {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '20px 18px',
              maxWidth: 362,
            },
          }}
        >
          {mobileImages.map((item) => (
            <MobileGridImage key={item.alt} src={item.src} alt={item.alt} />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          [HOME_MOBILE_MQ]: { display: 'none' },
          position: 'relative',
          zIndex: 2,
          width: '100vw',
          maxWidth: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          overflow: 'hidden',
          clipPath: `url(#${PAST_EVENTS_CLIP_ID})`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: { xs: '20px', lg: `${SLIDE_GAP}px` },
            width: 'max-content',
            animation: `${marquee} ${SLIDER_MARQUEE_DURATION}s linear infinite`,
            willChange: 'transform',
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
            '&:hover': {
              animationPlayState: 'paused',
            },
          }}
        >
          {loopImages.map((item, index) => (
            <GallerySlide key={`${item.alt}-${index}`} src={item.src} alt={item.alt} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
